import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  FiSearch, FiUser, FiUsers, FiCalendar, FiDollarSign, FiSettings,
  FiFileText, FiAward, FiClock, FiCreditCard, FiMapPin, FiHeart,
  FiTrendingUp, FiBook, FiGrid, FiShield, FiPackage, FiCheckSquare,
  FiArrowRight, FiCommand,
} from 'react-icons/fi'
import employeeService from '@/services/employeeService'

const HR_ADMIN = ['hr_manager', 'super_admin', 'company_admin']
const MANAGER_UP = ['department_manager', ...HR_ADMIN]

// All navigable pages with search keywords
const PAGES = [
  { path: '/', label: '首頁', keywords: '首頁 home dashboard 儀表板', icon: FiGrid },
  { path: '/my-hub', label: '我的專區', keywords: '我的 my hub 專區', icon: FiUser },
  { path: '/my-profile', label: '我的資料', keywords: '個人 profile 資料 銀行 緊急', icon: FiUser },
  { path: '/my-attendance', label: '我的出勤', keywords: '出勤 打卡 attendance', icon: FiClock },
  { path: '/my-payslip', label: '我的薪資條', keywords: '薪資 payslip 薪水', icon: FiDollarSign },
  { path: '/my-rewards', label: '我的獎懲', keywords: '獎懲 reward penalty', icon: FiAward },
  { path: '/directory', label: '員工目錄', keywords: '目錄 directory 找人 搜尋員工', icon: FiUsers },
  { path: '/notifications', label: '通知中心', keywords: '通知 notification 訊息', icon: FiFileText },
  { path: '/attendance', label: '出勤打卡', keywords: '打卡 出勤 check in clock', icon: FiClock },
  { path: '/attendance-corrections', label: '打卡補登', keywords: '補登 correction 忘打卡', icon: FiCheckSquare },
  { path: '/leave', label: '請假管理', keywords: '請假 leave 假期 年假 病假 事假', icon: FiCalendar },
  { path: '/overtime', label: '加班管理', keywords: '加班 overtime 補休', icon: FiClock },
  { path: '/swap-requests', label: '換班申請', keywords: '換班 swap 調班', icon: FiClock },
  { path: '/expenses', label: '費用管理', keywords: '費用 expense 報銷 報帳', icon: FiCreditCard },
  { path: '/business-trips', label: '差旅管理', keywords: '差旅 出差 travel trip', icon: FiMapPin },
  { path: '/calendar', label: '行事曆', keywords: '行事曆 calendar 日曆', icon: FiCalendar },
  { path: '/meetings', label: '會議管理', keywords: '會議 meeting 預約', icon: FiCalendar },
  { path: '/announcements', label: '公告', keywords: '公告 announcement 通知', icon: FiFileText },
  { path: '/surveys', label: '員工問卷', keywords: '問卷 survey 調查', icon: FiFileText },
  { path: '/employees', label: '員工管理', keywords: '員工 employee 人員', icon: FiUsers, roles: MANAGER_UP },
  { path: '/departments', label: '部門管理', keywords: '部門 department', icon: FiUsers, roles: HR_ADMIN },
  { path: '/positions', label: '職位管理', keywords: '職位 position 職稱', icon: FiUsers, roles: HR_ADMIN },
  { path: '/recruitment', label: '招聘管理', keywords: '招聘 recruitment 應徵 履歷', icon: FiUser, roles: HR_ADMIN },
  { path: '/offer-letters', label: 'Offer 管理', keywords: 'offer letter 錄取', icon: FiFileText, roles: HR_ADMIN },
  { path: '/onboarding', label: '到職管理', keywords: '到職 onboarding 新人', icon: FiCheckSquare, roles: HR_ADMIN },
  { path: '/offboarding-records', label: '離職管理', keywords: '離職 offboarding 資遣 resign', icon: FiUser, roles: HR_ADMIN },
  { path: '/performance', label: '績效考核', keywords: '績效 performance KPI 考核', icon: FiAward },
  { path: '/development', label: '員工發展', keywords: '發展 development IDP 訓練', icon: FiTrendingUp },
  { path: '/training', label: '培訓管理', keywords: '培訓 training 課程', icon: FiBook },
  { path: '/payroll', label: '薪資管理', keywords: '薪資 payroll 發薪', icon: FiDollarSign, roles: HR_ADMIN },
  { path: '/insurance', label: '勞健保管理', keywords: '勞保 健保 insurance 投保', icon: FiShield, roles: HR_ADMIN },
  { path: '/government-forms', label: '法定報表', keywords: '扣繳 憑單 報表 government 勞保 健保', icon: FiFileText, roles: HR_ADMIN },
  { path: '/severance', label: '資遣費管理', keywords: '資遣 severance 遣散', icon: FiDollarSign, roles: HR_ADMIN },
  { path: '/equipment', label: '資產管理', keywords: '資產 asset equipment 設備 筆電', icon: FiPackage, roles: HR_ADMIN },
  { path: '/company-policies', label: '企業政策', keywords: '政策 policy 規則 設定', icon: FiSettings, roles: HR_ADMIN },
  { path: '/holiday-calendar', label: '假期行事曆', keywords: '假期 holiday 國定 補班', icon: FiCalendar, roles: HR_ADMIN },
  { path: '/settings', label: '系統設定', keywords: '設定 settings 偏好', icon: FiSettings, roles: HR_ADMIN },
  { path: '/health-checkups', label: '健康檢查', keywords: '健檢 health checkup 體檢', icon: FiHeart },
]

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [employees, setEmployees] = useState([])
  const [empLoading, setEmpLoading] = useState(false)
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const { user } = useSelector((s) => s.auth)
  const role = user?.role

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIdx(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Search employees when query looks like a name
  useEffect(() => {
    if (!query || query.length < 2) { setEmployees([]); return }
    const timer = setTimeout(async () => {
      try {
        setEmpLoading(true)
        const r = await employeeService.getDirectory
          ? employeeService.getDirectory({ search: query, limit: 5 })
          : employeeService.getAll({ search: query, limit: 5 })
        const items = r.data?.data?.items || r.data?.data || []
        setEmployees(Array.isArray(items) ? items : [])
      } catch {
        setEmployees([])
      } finally {
        setEmpLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  // Filter pages by query + role
  const filteredPages = useMemo(() => {
    return PAGES.filter((p) => {
      if (p.roles && (!role || !p.roles.includes(role))) return false
      if (!query) return true
      const q = query.toLowerCase()
      return p.label.toLowerCase().includes(q) || p.keywords.toLowerCase().includes(q)
    }).slice(0, 10)
  }, [query, role])

  const allResults = useMemo(() => {
    const results = []
    // Employees first
    employees.forEach((emp) => {
      results.push({
        type: 'employee',
        label: `${emp.last_name || ''}${emp.first_name || ''} (${emp.employee_code || ''})`,
        sublabel: emp.department_name || '',
        path: `/employees`, // navigate to employees page (could enhance to employee detail)
        icon: FiUser,
      })
    })
    // Then pages
    filteredPages.forEach((p) => {
      results.push({
        type: 'page',
        label: p.label,
        sublabel: '頁面',
        path: p.path,
        icon: p.icon,
      })
    })
    return results
  }, [employees, filteredPages])

  const onSelect = useCallback((item) => {
    setOpen(false)
    navigate(item.path)
  }, [navigate])

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((prev) => Math.min(prev + 1, allResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && allResults[selectedIdx]) {
      e.preventDefault()
      onSelect(allResults[selectedIdx])
    }
  }, [allResults, selectedIdx, onSelect])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <FiSearch className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIdx(0) }}
            onKeyDown={onKeyDown}
            placeholder="搜尋頁面、功能或員工…"
            className="flex-1 text-base outline-none placeholder-gray-400"
          />
          <kbd className="hidden sm:inline-flex px-2 py-0.5 text-xs text-gray-400 border border-gray-200 rounded bg-gray-50">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {allResults.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {query ? '找不到相關結果' : '輸入關鍵字搜尋功能或員工'}
            </div>
          ) : (
            <div className="py-2">
              {allResults.map((item, idx) => {
                const Icon = item.icon
                return (
                  <button
                    key={`${item.type}-${item.path}-${idx}`}
                    onClick={() => onSelect(item)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left transition ${
                      idx === selectedIdx ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 opacity-60" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.label}</div>
                      {item.sublabel && <div className="text-xs text-gray-500">{item.sublabel}</div>}
                    </div>
                    <FiArrowRight className="w-4 h-4 opacity-40" />
                  </button>
                )
              })}
            </div>
          )}
          {empLoading && <div className="px-5 py-2 text-xs text-gray-400">搜尋員工中…</div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">↑↓</kbd> 移動</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">Enter</kbd> 前往</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">Esc</kbd> 關閉</span>
        </div>
      </div>
    </div>
  )
}
